import { z } from 'zod';

/**
 * Contenido de la landing de una campaña.
 *
 * Origen de cada texto:
 * - "documento": sale de los documentos de contexto (cita el documento) → aprobado de entrada.
 * - "piezas": propuesta de Claude a partir de lo que se ve en las piezas (formatos, copys
 *   visibles, imagen). Queda en `proposals` y el admin debe aprobarla; al publicar, las
 *   propuestas no aprobadas se quitan. Nunca se aceptan cifras ni resultados no documentados.
 */

const text = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) => text(max).nullable();

/** Lo que se le pide a Claude (salida estructurada). Esquema plano a propósito: la gramática
 * de salida estructurada tiene un límite de complejidad. El origen de cada texto se declara aparte,
 * en `documentSources`; todo texto que no aparezca ahí se trata como propuesta basada en piezas. */
export const ClaudeLandingSchema = z.object({
  hero: z.object({
    title: optionalText(120),
    subtitle: optionalText(200),
    summary: optionalText(1200),
  }),
  heroAssetId: z.string().nullable(),
  objective: optionalText(1500),
  concept: z.object({
    title: optionalText(160),
    body: optionalText(3000),
  }),
  sections: z
    .array(
      z.object({
        title: text(120),
        folderPaths: z.array(z.string()),
        description: optionalText(1200),
        justification: optionalText(3000),
      }),
    )
    .max(40),
  pieceNotes: z.array(z.object({ assetId: z.string(), text: text(1500) })).max(120),
  documentSources: z
    .array(z.object({ field: z.string(), sourceDoc: z.string() }))
    .describe(
      'Campos cuyo texto sale de un documento de contexto. field usa: hero.title, hero.subtitle, hero.summary, objective, concept.title, concept.body, sections.N.description, sections.N.justification (N = posición en sections, desde 0) o pieceNotes.<assetId>.',
    ),
});

export type ClaudeLanding = z.infer<typeof ClaudeLandingSchema>;

/** Contenido guardado en Firestore (borrador y publicado). */
export const LandingContentSchema = z.object({
  hero: z.object({
    title: text(120),
    subtitle: optionalText(200),
    summary: optionalText(1200),
  }),
  heroAssetId: z.string().nullable().default(null),
  objective: optionalText(1500),
  concept: z.object({
    title: optionalText(160),
    body: optionalText(3000),
  }),
  sections: z
    .array(
      z.object({
        id: z.string().regex(/^[a-z0-9-]{1,80}$/),
        title: text(120),
        description: optionalText(1200),
        justification: optionalText(3000),
        sourceDocs: z.array(z.string()),
        folderPaths: z.array(z.string()),
        assetIds: z.array(z.string()),
      }),
    )
    .min(1)
    .max(60),
  pieceNotes: z.array(
    z.object({
      assetId: z.string(),
      justification: text(1500),
      sourceDoc: z.string().nullable().default(null),
    }),
  ),
  /** Claves de campos propuestos por la IA y aún no aprobados (ver `fieldKey`). */
  proposals: z.array(z.string()).default([]),
});

export type LandingContent = z.infer<typeof LandingContentSchema>;
export type LandingSection = LandingContent['sections'][number];

/** Claves de campo estables para pendientes y propuestas. */
export const fieldKey = {
  heroTitle: 'hero.title',
  heroSubtitle: 'hero.subtitle',
  heroSummary: 'hero.summary',
  objective: 'objective',
  conceptTitle: 'concept.title',
  conceptBody: 'concept.body',
  sectionDescription: (id: string) => `sections.${id}.description`,
  sectionJustification: (id: string) => `sections.${id}.justification`,
  pieceNote: (assetId: string) => `pieceNotes.${assetId}`,
} as const;

export interface LandingAsset {
  id: string;
  name: string;
  path: string;
}

export interface NormalizeInput {
  campaignName: string;
  assets: LandingAsset[];
  docNames: string[];
  /** IDs de las piezas que Claude vio como imagen (candidatas a portada). */
  shownAssetIds?: string[];
}

export interface NormalizeResult {
  content: LandingContent;
  warnings: string[];
}

const stripAccents = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

export function slugify(value: string): string {
  const slug = stripAccents(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return slug || 'seccion';
}

const sortAssets = (a: LandingAsset, b: LandingAsset) =>
  a.path.localeCompare(b.path, 'es') || a.name.localeCompare(b.name, 'es', { numeric: true });

/** Título legible para una ruta: "Fase 1/01. Meta Ads" → "Fase 1 · 01. Meta Ads". */
export const pathTitle = (path: string): string =>
  path ? path.split('/').join(' · ') : 'Entregables';

/**
 * Convierte la salida de Claude en contenido guardable:
 * - asigna cada pieza a la sección cuya ruta coincide (la más específica gana);
 * - descarta rutas, IDs y documentos que no existen;
 * - marca como propuesta todo texto que no esté respaldado por un documento real;
 * - agrupa las piezas sin sección por su carpeta de primer nivel.
 */
export function normalizeLanding(raw: ClaudeLanding, input: NormalizeInput): NormalizeResult {
  const warnings: string[] = [];
  const proposals = new Set<string>();
  const assets = [...input.assets].sort(sortAssets);
  const assetIds = new Set(assets.map((a) => a.id));
  const knownPaths = new Set(assets.map((a) => a.path));
  const knownDocs = new Set(input.docNames);

  // Campos declarados como provenientes de un documento (solo cuentan documentos reales).
  const docByField = new Map<string, string>();
  for (const src of raw.documentSources) {
    if (knownDocs.has(src.sourceDoc)) docByField.set(src.field, src.sourceDoc);
    else
      warnings.push(
        `Un texto citaba un documento inexistente y quedó como propuesta (${src.field}).`,
      );
  }

  /** Acepta el texto; si no tiene un documento real como fuente, lo registra como propuesta. */
  const take = (
    value: string | null,
    rawField: string,
    key: string = rawField,
  ): { text: string | null; doc: string | null } => {
    if (!value) return { text: null, doc: null };
    const doc = docByField.get(rawField);
    if (doc) return { text: value, doc };
    proposals.add(key);
    return { text: value, doc: null };
  };

  const isKnownPrefix = (p: string) =>
    p === '' ? knownPaths.has('') : [...knownPaths].some((k) => k === p || k.startsWith(`${p}/`));

  const usedIds = new Set<string>();
  const uniqueId = (title: string) => {
    const base = slugify(title);
    let id = base;
    for (let n = 2; usedIds.has(id); n++) id = `${base}-${n}`;
    usedIds.add(id);
    return id;
  };

  const sections = raw.sections.map((s, rawIndex) => {
    const folderPaths = [...new Set(s.folderPaths.map((p) => p.replace(/^\/+|\/+$/g, '')))].filter(
      (p) => {
        const ok = isKnownPrefix(p);
        if (!ok) warnings.push(`La sección "${s.title}" citaba una carpeta inexistente: "${p}".`);
        return ok;
      },
    );
    return { raw: s, rawIndex, title: s.title, folderPaths, assetIds: [] as string[] };
  });

  // Asignación determinista: la ruta de sección más larga que contenga a la pieza.
  const unassigned: LandingAsset[] = [];
  for (const asset of assets) {
    let best: { section: (typeof sections)[number]; length: number } | null = null;
    for (const section of sections) {
      for (const p of section.folderPaths) {
        const matches =
          p === '' ? asset.path === '' : asset.path === p || asset.path.startsWith(`${p}/`);
        if (matches && (!best || p.length > best.length)) best = { section, length: p.length };
      }
    }
    if (best) best.section.assetIds.push(asset.id);
    else unassigned.push(asset);
  }

  const finalSections: LandingSection[] = [];
  let emptySections = 0;
  for (const s of sections) {
    if (s.assetIds.length === 0) {
      emptySections++;
      continue;
    }
    const id = uniqueId(s.title);
    const description = take(
      s.raw.description,
      `sections.${s.rawIndex}.description`,
      fieldKey.sectionDescription(id),
    );
    const justification = take(
      s.raw.justification,
      `sections.${s.rawIndex}.justification`,
      fieldKey.sectionJustification(id),
    );
    finalSections.push({
      id,
      title: s.title,
      description: description.text,
      justification: justification.text,
      sourceDocs: [
        ...new Set([description.doc, justification.doc].filter((d): d is string => Boolean(d))),
      ],
      folderPaths: s.folderPaths,
      assetIds: s.assetIds,
    });
  }
  if (emptySections > 0) warnings.push(`Se omitieron ${emptySections} secciones sin piezas.`);

  // Piezas que Claude no ubicó: secciones por carpeta de primer nivel, sin texto.
  const extra = new Map<string, string[]>();
  for (const asset of unassigned) {
    const top = asset.path.split('/')[0] ?? '';
    extra.set(top, [...(extra.get(top) ?? []), asset.id]);
  }
  if (unassigned.length > 0) {
    warnings.push(
      `${unassigned.length} piezas no estaban en ninguna sección y se agruparon por carpeta.`,
    );
  }
  for (const [top, ids] of extra) {
    finalSections.push({
      id: uniqueId(pathTitle(top)),
      title: pathTitle(top),
      description: null,
      justification: null,
      sourceDocs: [],
      folderPaths: [top],
      assetIds: ids,
    });
  }
  if (finalSections.length === 0) {
    finalSections.push({
      id: 'entregables',
      title: 'Entregables',
      description: null,
      justification: null,
      sourceDocs: [],
      folderPaths: [],
      assetIds: [],
    });
  }

  const seenNotes = new Set<string>();
  const pieceNotes: LandingContent['pieceNotes'] = [];
  for (const n of raw.pieceNotes) {
    if (!assetIds.has(n.assetId)) {
      warnings.push('Se descartó una nota de pieza con un ID inexistente.');
      continue;
    }
    if (seenNotes.has(n.assetId)) continue;
    seenNotes.add(n.assetId);
    const taken = take(n.text, fieldKey.pieceNote(n.assetId));
    if (taken.text)
      pieceNotes.push({ assetId: n.assetId, justification: taken.text, sourceDoc: taken.doc });
  }

  const title = take(raw.hero.title, fieldKey.heroTitle);
  const shown = new Set(input.shownAssetIds ?? []);
  const heroAssetId =
    raw.heroAssetId &&
    assetIds.has(raw.heroAssetId) &&
    (shown.size === 0 || shown.has(raw.heroAssetId))
      ? raw.heroAssetId
      : null;

  const content: LandingContent = {
    // El nombre de la campaña (lo escribió el admin) es el respaldo del título.
    hero: {
      title: title.text ?? input.campaignName,
      subtitle: take(raw.hero.subtitle, fieldKey.heroSubtitle).text,
      summary: take(raw.hero.summary, fieldKey.heroSummary).text,
    },
    heroAssetId,
    objective: take(raw.objective, fieldKey.objective).text,
    concept: {
      title: take(raw.concept.title, fieldKey.conceptTitle).text,
      body: take(raw.concept.body, fieldKey.conceptBody).text,
    },
    sections: finalSections,
    pieceNotes,
    proposals: [...proposals],
  };
  return { content: LandingContentSchema.parse(content), warnings };
}

/**
 * Versión publicable: quita las propuestas de la IA que el admin no aprobó.
 * El título nunca queda vacío: si era una propuesta, vuelve al nombre de la campaña.
 */
export function stripProposals(content: LandingContent, campaignName: string): LandingContent {
  const p = new Set(content.proposals);
  const keep = (key: string, value: string | null) => (p.has(key) ? null : value);
  return {
    ...content,
    hero: {
      title: p.has(fieldKey.heroTitle) ? campaignName : content.hero.title,
      subtitle: keep(fieldKey.heroSubtitle, content.hero.subtitle),
      summary: keep(fieldKey.heroSummary, content.hero.summary),
    },
    objective: keep(fieldKey.objective, content.objective),
    concept: {
      title: keep(fieldKey.conceptTitle, content.concept.title),
      body: keep(fieldKey.conceptBody, content.concept.body),
    },
    sections: content.sections.map((s) => ({
      ...s,
      description: keep(fieldKey.sectionDescription(s.id), s.description),
      justification: keep(fieldKey.sectionJustification(s.id), s.justification),
    })),
    pieceNotes: content.pieceNotes.filter((n) => !p.has(fieldKey.pieceNote(n.assetId))),
    proposals: [],
  };
}

export interface PendingField {
  /** Ruta del campo, p. ej. "objective" o "sections.redes.justification". */
  key: string;
  label: string;
}

/** Campos sin contenido: se marcan como pendientes en el editor y no se muestran al cliente. */
export function pendingFields(content: LandingContent): PendingField[] {
  const pending: PendingField[] = [];
  if (!content.hero.subtitle) pending.push({ key: fieldKey.heroSubtitle, label: 'Subtítulo' });
  if (!content.hero.summary)
    pending.push({ key: fieldKey.heroSummary, label: 'Resumen de la campaña' });
  if (!content.objective) pending.push({ key: fieldKey.objective, label: 'Objetivo' });
  if (!content.concept.title)
    pending.push({ key: fieldKey.conceptTitle, label: 'Título del concepto' });
  if (!content.concept.body)
    pending.push({ key: fieldKey.conceptBody, label: 'Concepto creativo' });
  for (const s of content.sections) {
    if (!s.justification) {
      pending.push({
        key: fieldKey.sectionJustification(s.id),
        label: `Justificación · ${s.title}`,
      });
    }
  }
  return pending;
}
